import type Phaser from 'phaser';

/**
 * Text drawn from the 5×7 bitmap font, one sprite per glyph.
 *
 * Phaser's Text goes through canvas `fillText`, which anti-aliases whatever
 * the settings say; magnified by the camera that turns into grey mush. Glyph
 * sprites are just pixels, so they scale exactly like the rest of the world.
 */

export const GLYPH_WIDTH = 5;
export const GLYPH_HEIGHT = 7;
/** The compact `font.pixel-small` sheet. */
export const SMALL_GLYPH_WIDTH = 3;
export const SMALL_GLYPH_HEIGHT = 5;
/** One blank column between glyphs; the font itself carries no side bearing. */
const LETTER_SPACING = 1;
/** Two blank rows between lines, so the outlines of two lines never touch. */
const LINE_SPACING = 2;

export interface PixelTextOptions {
  scene: Phaser.Scene;
  atlasKey: string;
  /** Logical font key, e.g. `font.pixel`. */
  fontKey: string;
  color?: number;
  /** Dark outline behind the glyphs, so a name stays legible over any terrain. */
  outlineColor?: number;
  /** Pinned to the screen rather than the world; for status and UI text. */
  fixedToCamera?: boolean;
  /** Glyph cell of the font sheet; defaults to the 5×7 name-plate font. */
  glyphWidth?: number;
  glyphHeight?: number;
}

export class PixelText {
  readonly container: Phaser.GameObjects.Container;
  private readonly options: PixelTextOptions;
  private readonly glyphs: Phaser.GameObjects.Image[] = [];
  private readonly outlines: Phaser.GameObjects.Image[] = [];
  private text = '';
  private glyphCount = 0;
  private lines = 1;
  private color: number | undefined;
  readonly glyphWidth: number;
  readonly glyphHeight: number;

  constructor(options: PixelTextOptions) {
    this.options = options;
    this.color = options.color;
    this.glyphWidth = options.glyphWidth ?? GLYPH_WIDTH;
    this.glyphHeight = options.glyphHeight ?? GLYPH_HEIGHT;
    this.container = options.scene.add.container(0, 0);
    if (options.fixedToCamera === true) this.container.setScrollFactor(0);
  }

  /** Width in pixels of the text as currently laid out. */
  get width(): number {
    const count = this.glyphCount;
    return count === 0 ? 0 : count * (this.glyphWidth + LETTER_SPACING) - LETTER_SPACING;
  }

  /** Lines as laid out: `\n` in the text starts a new one, each centred. */
  get lineCount(): number {
    return this.lines;
  }

  /** Pixels from one line's top to the next's. */
  get lineStep(): number {
    return this.glyphHeight + LINE_SPACING;
  }

  setVisible(visible: boolean): void {
    this.container.setVisible(visible);
  }

  setDepth(depth: number): void {
    this.container.setDepth(depth);
  }

  /** Retint the glyphs in place (a name plate taking its owner's aura colour). */
  setColor(color: number): void {
    if (color === this.color) return;
    this.color = color;
    for (const glyph of this.glyphs) glyph.setTint(color);
  }

  /** Uppercased: the font has no lowercase, and a box per letter would be worse. */
  setText(value: string): void {
    const wanted = value.toUpperCase();
    if (wanted === this.text) return;
    this.text = wanted;
    this.render();
  }

  private frameFor(character: string): string | null {
    const texture = this.options.scene.textures.get(this.options.atlasKey);
    const lookup = (value: string): string | null => {
      const code = value.codePointAt(0);
      if (code === undefined) return null;
      const frame = `${this.options.fontKey}/${String(code)}`;
      return texture.has(frame) ? frame : null;
    };
    // A font without accented glyphs (the small one) shows the base letter:
    // "RAT DES ÉGOUTS" reads fine as "RAT DES EGOUTS", a box would not.
    return lookup(character) ?? lookup(character.normalize('NFD').replace(/\p{M}/gu, ''));
  }

  private render(): void {
    for (const image of [...this.glyphs, ...this.outlines]) image.destroy();
    this.glyphs.length = 0;
    this.outlines.length = 0;

    const { scene, atlasKey } = this.options;
    // Intl.Segmenter keeps a grapheme whole; a nickname is validated to plain
    // letters and digits, but this is the correct way to walk text.
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    const rows = this.text
      .split('\n')
      .map((row) => [...segmenter.segment(row)].map((entry) => entry.segment));
    this.lines = rows.length;
    // The widest line, for `width`: a name plate is one line, a bubble several.
    this.glyphCount = Math.max(...rows.map((row) => row.length));

    rows.forEach((characters, row) => {
      const width = characters.length * (this.glyphWidth + LETTER_SPACING) - LETTER_SPACING;
      // Centre on a whole pixel: an odd width would otherwise land the whole
      // label on a half pixel and undo the point of a bitmap font.
      let x = -Math.round(width / 2);
      const y = row * this.lineStep;
      for (const character of characters) {
        const frame = this.frameFor(character);
        if (frame !== null) {
          // Four offset copies make the outline: cheaper than a second atlas and
          // it follows any glyph shape exactly.
          if (this.options.outlineColor !== undefined) {
            for (const [dx, dy] of [
              [-1, 0],
              [1, 0],
              [0, -1],
              [0, 1],
            ] as const) {
              const outline = scene.add
                .image(x + dx, y + dy, atlasKey, frame)
                .setOrigin(0, 0)
                .setTint(this.options.outlineColor);
              this.container.add(outline);
              this.outlines.push(outline);
            }
          }
          const glyph = scene.add.image(x, y, atlasKey, frame).setOrigin(0, 0);
          if (this.color !== undefined) glyph.setTint(this.color);
          this.container.add(glyph);
          this.glyphs.push(glyph);
        }
        x += this.glyphWidth + LETTER_SPACING;
      }
    });
  }

  setPosition(x: number, y: number): void {
    this.container.setPosition(Math.round(x), Math.round(y));
  }

  destroy(): void {
    this.container.destroy(true);
  }
}
